import React, { PropsWithChildren, useMemo } from 'react';

import classNames from 'classnames';
import { isEmptyString } from '@/utils/formatChecker';
import { GetArticleBodyByType } from '@/constants/article';

type KeywordType = 'hypertext' | 'highlight';

interface Keyword {
  type: KeywordType;
  keyword: string;
  href?: string;
  start: number;
  end: number;
}

const findKeywords = (
  content: string,
  hypertext: Array<{ keyword: string; href: string }> = [],
  highlight: Array<{ keyword: string }> = []
): Array<Keyword> => {
  if (isEmptyString(content)) return [];

  const keywords: Array<Keyword> = [];

  hypertext.forEach(({ keyword, href }) => {
    if (isEmptyString(keyword)) return;
    let start = content.indexOf(keyword);
    while (start !== -1) {
      keywords.push({
        type: 'hypertext',
        keyword,
        href,
        start,
        end: start + keyword.length,
      });
      start = content.indexOf(keyword, start + 1);
    }
  });

  highlight.forEach(({ keyword }) => {
    if (isEmptyString(keyword)) return;
    let start = content.indexOf(keyword);
    while (start !== -1) {
      keywords.push({
        type: 'highlight',
        keyword,
        start,
        end: start + keyword.length,
      });
      start = content.indexOf(keyword, start + 1);
    }
  });

  return keywords;
};

const insertTags = (content: string, keywords: Array<Keyword>): string => {
  if (isEmptyString(content)) return content;

  const tags: Array<{ pos: number; tag: string }> = [];

  keywords.forEach(({ type, href, start, end }) => {
    if (type === 'hypertext') {
      tags.push({
        pos: start,
        // eslint-disable-next-line max-len
        tag: `<a class="text-gold underline" href="${href}" target="_blank" rel="noreferrer noopener">`,
      });
      tags.push({ pos: end, tag: `</a>` });
    } else if (type === 'highlight') {
      tags.push({ pos: start, tag: `<b>` });
      tags.push({ pos: end, tag: `</b>` });
    }
  });

  tags.sort((a, b) => a.pos - b.pos);

  let offset = 0;
  tags.forEach(({ pos, tag }) => {
    content = content.slice(0, pos + offset) + tag + content.slice(pos + offset);
    offset += tag.length;
  });

  return content;
};

export const Body: React.FC<GetArticleBodyByType<'body'>> = ({ content, hypertext, highlight }) => {
  const serializedContent = useMemo(() => content.join('\n'), [content]);
  const keywords = useMemo(
    () => findKeywords(serializedContent, hypertext, highlight),
    [serializedContent, JSON.stringify(hypertext), JSON.stringify(highlight)]
  );

  const htmlContent = useMemo(() => insertTags(serializedContent, keywords), [serializedContent, keywords]);

  return (
    <div className={'mt-4 md:mt-8'}>
      <Paragraph dangerouslySetInnerHTML={{ __html: htmlContent }} />
    </div>
  );
};

export const Paragraph: React.FC<PropsWithChildren<JSX.IntrinsicElements['p']>> = ({ children, ...props }) => {
  return (
    <p className={classNames('md:text-lg text-gray800 text-base', 'whitespace-pre-line')} {...props}>
      {children}
    </p>
  );
};
